import React from "react";
import { Download, Share, Plus, X, Smartphone } from "lucide-react";
import { useInstallApp } from "../hooks/useInstallApp";

// Tiny circular icon button (e.g. inside the Header)
export const InstallAppIconButton = () => {
    const { canInstall, triggerInstall } = useInstallApp();
    if (!canInstall) return null;
    return (
        <button
            type="button"
            onClick={triggerInstall}
            className="w-9 h-9 rounded-full border border-[#7c3aed]/50 bg-[#150826]/70 text-[#e879f9] hover:border-[#e879f9] hover:shadow-[0_0_14px_rgba(232,121,249,0.5)] active:scale-90 transition-all flex items-center justify-center"
            data-testid="header-install"
            aria-label="Installa app"
            title="Installa app"
        >
            <Download className="w-3.5 h-3.5" />
        </button>
    );
};

// Full pill button (e.g. for the Splash CTA area)
export const InstallAppPillButton = ({ label = "Scarica App" }) => {
    const { canInstall, triggerInstall } = useInstallApp();
    if (!canInstall) return null;
    return (
        <button
            type="button"
            onClick={triggerInstall}
            data-testid="splash-install-app"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full border border-[#7c3aed]/55 bg-[#150826]/70 text-[#e879f9] hover:border-[#e879f9] hover:bg-[#1f0f36] hover:shadow-[0_0_18px_rgba(232,121,249,0.4)] active:scale-95 transition-all"
        >
            <Download className="w-4 h-4" />
            <span className="font-mono uppercase tracking-[0.18em] text-xs font-semibold">{label}</span>
        </button>
    );
};

// Top banner shown at the entrance (above splash content)
export const InstallBanner = () => {
    const { canInstall, triggerInstall } = useInstallApp();
    const [dismissed, setDismissed] = React.useState(() => {
        try { return localStorage.getItem("install_banner_dismissed") === "1"; } catch { return false; }
    });
    if (!canInstall || dismissed) return null;
    const dismiss = (e) => {
        e.stopPropagation();
        try { localStorage.setItem("install_banner_dismissed", "1"); } catch {}
        setDismissed(true);
    };
    return (
        <button
            type="button"
            onClick={triggerInstall}
            data-testid="install-banner"
            className="group relative w-full px-3 py-2.5 bg-gradient-to-r from-[#7c3aed] via-[#a78bfa] to-[#e879f9] hover:from-[#e879f9] hover:to-[#a78bfa] shadow-[0_4px_18px_rgba(124,58,237,0.55)] active:scale-[0.99] transition-all overflow-hidden"
        >
            <div className="max-w-md mx-auto flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 text-left">
                    <Smartphone className="w-4 h-4 text-white shrink-0" />
                    <div className="flex flex-col leading-tight">
                        <span className="font-mono uppercase tracking-[0.14em] text-[11px] font-bold text-white">
                            Installa l'app sul telefono
                        </span>
                        <span className="font-mono text-[10px] text-white/85">
                            tocca per aggiungere alla schermata Home
                        </span>
                    </div>
                </div>
                <span
                    role="button"
                    aria-label="Chiudi"
                    onClick={dismiss}
                    className="shrink-0 w-6 h-6 rounded-full bg-black/30 hover:bg-black/50 flex items-center justify-center cursor-pointer"
                >
                    <X className="w-3 h-3 text-white" />
                </span>
            </div>
        </button>
    );
};

// Modal with step-by-step instructions for iOS or unsupported browsers
export const InstallGuideModal = () => {
    const { showIOSGuide, dismissIOSGuide, isIOS } = useInstallApp();
    if (!showIOSGuide) return null;
    return (
        <div
            className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-5"
            onClick={dismissIOSGuide}
            data-testid="install-guide"
        >
            <div
                className="relative max-w-sm w-full bg-[#0d0418] border-2 border-[#7c3aed] rounded-3xl p-6 shadow-[0_0_32px_rgba(124,58,237,0.6)]"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={dismissIOSGuide}
                    className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                    aria-label="Chiudi"
                    data-testid="install-guide-close"
                >
                    <X className="w-4 h-4" />
                </button>
                <div className="flex flex-col items-center text-center">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#a78bfa] to-[#7c3aed] flex items-center justify-center mb-4 shadow-[0_0_18px_rgba(168,139,250,0.5)]">
                        <Smartphone className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="font-serif-display text-2xl text-white mb-2">Aggiungi alla Home</h3>
                    <p className="text-white/65 text-sm leading-relaxed mb-5">
                        {isIOS
                            ? "Su iPhone/iPad segui questi passaggi per installare l'app:"
                            : "Per installare l'app sul tuo dispositivo segui questi passaggi:"}
                    </p>

                    <ol className="w-full flex flex-col gap-3 text-left mb-5">
                        <li className="flex items-start gap-3">
                            <span className="shrink-0 w-7 h-7 rounded-full bg-[#7c3aed]/30 border border-[#7c3aed] text-[#e879f9] font-mono font-bold text-sm flex items-center justify-center">1</span>
                            <div className="flex-1 text-sm text-white/85 flex items-center gap-2">
                                Tocca l'icona <Share className="inline w-4 h-4 text-[#a78bfa]" /> <span className="font-semibold">Condividi</span> nel browser
                            </div>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="shrink-0 w-7 h-7 rounded-full bg-[#7c3aed]/30 border border-[#7c3aed] text-[#e879f9] font-mono font-bold text-sm flex items-center justify-center">2</span>
                            <div className="flex-1 text-sm text-white/85 flex items-center gap-2">
                                Scegli <Plus className="inline w-4 h-4 text-[#a78bfa]" /> <span className="font-semibold">Aggiungi a Home</span>
                            </div>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="shrink-0 w-7 h-7 rounded-full bg-[#7c3aed]/30 border border-[#7c3aed] text-[#e879f9] font-mono font-bold text-sm flex items-center justify-center">3</span>
                            <div className="flex-1 text-sm text-white/85">
                                Conferma con <span className="font-semibold text-[#e879f9]">Aggiungi</span> — l'app comparirà tra le icone del telefono!
                            </div>
                        </li>
                    </ol>

                    <button
                        onClick={dismissIOSGuide}
                        className="w-full py-2.5 rounded-full bg-gradient-to-r from-[#7c3aed] to-[#9333ea] hover:from-[#a78bfa] hover:to-[#e879f9] text-white font-mono uppercase tracking-[0.15em] text-xs font-bold shadow-[0_4px_14px_rgba(124,58,237,0.5)] active:scale-95 transition-all"
                    >
                        Ho capito
                    </button>
                </div>
            </div>
        </div>
    );
};
